/**
 * How a URL and a language map onto each other, and the cookie that remembers
 * an explicit choice.
 *
 * Shared because both halves must give the same answer. The browser builds the
 * language switcher's links from these helpers; the server decides from them
 * whether an arriving visitor should be sent to the other tree. Two
 * implementations would eventually disagree, and the symptom of that
 * disagreement is a redirect loop.
 *
 * Keep it free of runtime dependencies and of anything Node-specific — it is
 * bundled into the browser.
 */
import { DEFAULT_LOCALE, isLocale, type Locale } from './waitlist.js';

/**
 * The cookie that remembers a language.
 *
 * It is written only when the visitor clicks the language switcher, never from
 * detection. That distinction is the whole legal basis for having no cookie
 * banner: a cookie storing a preference the visitor actively expressed is a
 * "user-interface customisation" cookie, which the ePrivacy Directive exempts
 * from consent. A cookie storing a *guess* would not be exempt.
 *
 * See src/client/legal/cookies.en.tsx, which has to keep saying the same thing.
 */
export const LANGUAGE_COOKIE = 'lang';

/** A year — long enough that the choice survives, short enough that it lapses. */
export const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** Read the locale out of a pathname. Exported so the router and tests agree. */
export function localeFromPath(pathname: string): Locale {
  const first = pathname.split('/').filter(Boolean)[0];
  return isLocale(first) ? first : DEFAULT_LOCALE;
}

/** Strip the locale segment, giving the path as it would be in English. */
export function stripLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && isLocale(segments[0])) segments.shift();
  return '/' + segments.join('/');
}

/** Build the equivalent URL in another language — used by the language switcher. */
export function pathInLocale(pathname: string, locale: Locale): string {
  const base = stripLocale(pathname);
  if (locale === DEFAULT_LOCALE) return base === '/' ? '/' : base;
  return base === '/' ? '/es' : `/es${base}`;
}

/**
 * The visitor's preferred language, from the `Accept-Language` header the
 * browser sends on its own — nothing is stored to learn this.
 *
 * Returns the first language we actually publish, in the visitor's own order of
 * preference, so someone who reads French then Spanish then English gets
 * Spanish. `null` means the header named nothing we speak, and the caller
 * should fall back to English rather than guess.
 */
export function preferredLocale(header: string | undefined | null): Locale | null {
  if (!header) return null;

  const ranked = header
    .split(',')
    .map((part) => {
      const [tag = '', ...params] = part.trim().split(';');
      const weight = params.find((param) => param.trim().startsWith('q='));
      // A malformed q= is worth less than no q= at all, so treat it as zero.
      const quality = weight ? Number.parseFloat(weight.trim().slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((entry) => entry.tag.length > 0 && entry.quality > 0)
    // A stable sort keeps equal weights in the order the visitor listed them,
    // which is exactly what the specification says they mean.
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of ranked) {
    // "*" means "anything"; it expresses no preference, so it decides nothing.
    if (tag === '*') return null;
    const base = tag.split('-')[0];
    if (isLocale(base)) return base;
  }

  return null;
}
